/**
 * Library Content
 * 
 * Main content area displaying search, forms, and library items.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useConfirm } from '../../../components/ui/AlertDialog';
import { Icon } from '../../../components/ui/Icon';
import FormModal from '../../../components/ui/FormModal/FormModal';
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/Tabs2';
import { useLibrary, ENTITY_TYPES } from '../LibraryContext';
import libraryService from '../../../services/libraryService';
import ItemTable from './ItemTable';
import { tableColumns } from '../config/tableColumns';
import { ENTITY_FORM_SPECS, isFormModalEntity } from '../config/formSpecs';
import { Button } from '../../../components/ui/Button2';
import { TypographyMuted } from '../../../components/ui/Typography';
import { notify } from '../../../services/notificationBus';
import styles from '../styles/Library.module.css';

export default function LibraryContent() {
  const { state, actions } = useLibrary();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const showToast = (type, message) => notify({ type, message });
  const { confirm, confirmDialog } = useConfirm();
  const [threatTab, setThreatTab] = useState('user');
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Persist the active section in the URL (?section=…) so it survives a
  // page refresh / is shareable. URL is the source of truth on load and
  // back/forward; the sidebar nav writes back to it. 'module' (the default)
  // is kept implicit to keep the URL clean.
  useEffect(() => {
    const s = searchParams.get('section');
    if (s && ENTITY_TYPES[s] && s !== state.currentSection) {
      actions.setCurrentSection(s);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const s = searchParams.get('section') || 'module';
    if (state.currentSection === s) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (state.currentSection === 'module') next.delete('section');
        else next.set('section', state.currentSection);
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentSection]);

  // Import modal — parse the chosen JSON for a live "N items ready" preview.
  // The ref guards against re-parsing the same File when other fields change.
  const [importPreview, setImportPreview] = useState(null);
  const lastParsedFile = useRef(null);
  const parseImportFile = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          resolve(JSON.parse(e.target.result));
        } catch {
          reject(new Error('not-json'));
        }
      };
      reader.onerror = () => reject(new Error('read-error'));
      reader.readAsText(file);
    });


  // Reference data — drives both the in-table reference pills and the
  // multi-select options inside FormModal-driven Create/Edit forms.
  const [modules, setModules] = useState([]);
  const [features, setFeatures] = useState([]);
  const [assets, setAssets] = useState([]);
  const [goals, setGoals] = useState([]);
  const [damages, setDamages] = useState([]);
  const [threats, setThreats] = useState([]);
  const [technologies, setTechnologies] = useState([]);

  // Load items when section, filters, or threat tab change
  /* Reload items when section / tag filter / sort / threat-tab change.
     `state.searchQuery` is intentionally NOT in the dep array — search
     is purely client-side filtering inside ItemTable's useMemo, and
     loadItems doesn't even pass the query to libraryService.getItems.
     Including it caused every keystroke to flip loading=true, blank
     the table, and re-fetch the same list, which read as a laggy
     re-render compared to ProjectsTable's smooth local-filter search. */
  useEffect(() => {
    loadItems();
  }, [state.currentSection, state.selectedTags, state.sortBy, threatTab]);

  // Load reference data for all entity types
  useEffect(() => {
    loadReferenceData();
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);

      let result;
      if (state.currentSection === 'threat' && threatTab === 'global') {
        result = await libraryService.getThreatCatalogue();
      } else {
        result = await libraryService.getItems(
          state.currentSection,
          state.selectedTags.length > 0 ? state.selectedTags : null,
          state.sortBy,
          state.sortOrder
        );
      }

      setItems(result.items || []);
      actions.setItems(state.currentSection, result.items || []);
    } catch (err) {
      console.error('Error loading items:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadReferenceData = async () => {
    try {
      // Load all entity types that might be referenced (in-table pills
      // + FormModal multi-select options).
      const [
        modulesRes,
        featuresRes,
        assetsRes,
        goalsRes,
        damagesRes,
        threatsRes,
        technologiesRes,
      ] = await Promise.all([
        libraryService.getItemsByType('module').catch(() => ({ items: [] })),
        libraryService.getItemsByType('feature').catch(() => ({ items: [] })),
        libraryService.getItemsByType('asset').catch(() => ({ items: [] })),
        libraryService.getItemsByType('goal').catch(() => ({ items: [] })),
        libraryService.getItemsByType('damage_scenario').catch(() => ({ items: [] })),
        libraryService.getItemsByType('threat').catch(() => ({ items: [] })),
        libraryService.getItemsByType('technology').catch(() => ({ items: [] })),
      ]);

      setModules(modulesRes.items || []);
      setFeatures(featuresRes.items || []);
      setAssets(assetsRes.items || []);
      setGoals(goalsRes.items || []);
      setDamages(damagesRes.items || []);
      setThreats(threatsRes.items || []);
      setTechnologies(technologiesRes.items || []);
    } catch (err) {
      console.error('Error loading reference data:', err);
    }
  };

  const handleSave = async (itemData) => {
    try {
      if (state.editingItem) {
        // Update existing item
        const updated = await libraryService.updateItem(
          state.editingItem.item_id,
          itemData
        );
        actions.updateItem(updated);
        showSuccessToast('Item updated successfully!');
      } else {
        // Create new item
        const created = await libraryService.createItem(itemData);
        actions.addItem(created);
        showSuccessToast('Item created successfully!');
      }

      actions.closeForm();
      await loadItems();
      // Refresh reference data to show updated names
      await loadReferenceData();
    } catch (err) {
      console.error('Error saving item:', err);
      showErrorToast(err.message || 'Failed to save item');
    }
  };

  const handleDelete = async (item) => {
    const ok = await confirm({
      title: 'Delete item?',
      description: `Delete "${getItemName(item)}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
      media: <Icon name="delete" />,
    });
    if (!ok) return;
    try {
      await libraryService.deleteItem(item.item_id);
      actions.deleteItem(state.currentSection, item.item_id);
      showSuccessToast('Item deleted successfully!');
      await loadItems();
      await loadReferenceData();
    } catch (err) {
      console.error('Error deleting item:', err);
      showErrorToast(err.message || 'Failed to delete item');
    }
  };

  const handleSearchChange = (query) => {
    actions.setSearchQuery(query);
  };

  const getItemName = (item) => {
    return item.name || 
           item.entity_data?.goalStatement || 
           item.entity_data?.claimStatement || 
           item.entity_data?.reqTitle || 
           'Untitled';
  };

  // Toast notification helpers
  const showSuccessToast = (message) => {
    showToast('success', message);
  };

  const showErrorToast = (message) => {
    showToast('error', message);
  };

  const currentEntityConfig = ENTITY_TYPES[state.currentSection];

  /* Athena convention: the primary "+ New X" CTA lives in the
     TableToolbar's actions slot (rendered by ItemTable), not in the
     page header. The library sidebar's active section already labels
     which entity type the user is on, so the duplicate page-level
     title was redundant and is removed. Only the Global/User tab bar
     (threats only) lives above the table. */
  return (
    <main className={styles.content}>
      {state.currentSection === 'threat' && (
        <div className={styles.threatTabBar}>
          <Tabs value={threatTab} onValueChange={setThreatTab}>
            <TabsList variant="line" className={styles.threatTabsList}>
              <TabsTrigger value="user">User</TabsTrigger>
              <TabsTrigger value="global">Global</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      )}

      <div className={styles.tableArea}>
        {error ? (
          <div className={styles.errorState}>
            <h3>Error loading items</h3>
            <p>{error}</p>
            <button onClick={loadItems}>Retry</button>
          </div>
        ) : (
          /* While fetching, the table stays mounted and DataTable renders
             its skeleton rows (loading prop) — no page-level spinner swap. */
          <ItemTable
            items={items}
            loading={loading}
            columns={tableColumns[state.currentSection]}
            onEdit={(item) => actions.openForm(item)}
            onDelete={handleDelete}
            onCreateClick={() => actions.openForm()}
            createLabel={`New ${currentEntityConfig?.label || 'Item'}`}
            searchTerm={state.searchQuery}
            onSearchChange={handleSearchChange}
            entityType={state.currentSection}
            referenceData={{
              modules,
              features,
              assets,
              goals,
              damages,
              threats,
            }}
            readOnly={state.currentSection === 'threat' && threatTab === 'global'}
          />
        )}
      </div>


      {/* Add / Edit Record Modal. All 10 entity types drive the form via the
          shared FormModal primitive + ENTITY_FORM_SPECS — one declarative
          `fields` array per entity, multi-select options closed over the loaded
          reference data. Threat's spec (threatFormSpec.js) uses FormModal's
          `custom` field type for its live feasibility-score preview. */}
      {state.isFormOpen && isFormModalEntity(state.currentSection) && (() => {
        const spec = ENTITY_FORM_SPECS[state.currentSection];
        const refs = { modules, features, assets, goals, damages, threats, technologies };
        return (
          <FormModal
            title={state.editingItem ? `Edit ${spec.label}` : `Create New ${spec.label}`}
            fields={spec.fields(refs)}
            initialValues={spec.initialValues(state.editingItem)}
            submitLabel={state.editingItem ? 'Save Changes' : 'Save to Library'}
            submittingLabel="Saving..."
            maxWidth={720}
            onSubmit={async (values) => { await handleSave(spec.toPayload(values)); }}
            onClose={() => actions.closeForm()}
          />
        );
      })()}

      {state.isExportModalOpen && (() => {
        const allKeys = Object.keys(ENTITY_TYPES);
        const exportFields = [
          {
            name: '_selectAll',
            type: 'custom',
            render: (values, { setValue }) => {
              const all = (values.entityTypes || []).length === allKeys.length;
              return (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={() => setValue('entityTypes', all ? [] : allKeys)}
                >
                  {all ? 'Deselect all' : 'Select all'}
                </Button>
              );
            },
          },
          {
            name: 'entityTypes',
            type: 'checkbox-group',
            label: 'Entity types',
            required: true,
            options: allKeys.map((k) => ({ value: k, label: ENTITY_TYPES[k].plural })),
          },
          {
            name: 'format',
            type: 'select',
            label: 'Format',
            options: [
              { value: 'json', label: 'JSON (recommended)' },
              { value: 'csv', label: 'CSV (basic info only)' },
            ],
          },
        ];
        return (
          <FormModal
            title="Export Library"
            fields={exportFields}
            initialValues={{ entityTypes: allKeys, format: 'json' }}
            submitLabel="Export Library"
            submittingLabel="Exporting..."
            onSubmit={async (v) => {
              const data = await libraryService.exportLibrary(v.entityTypes, v.format);
              const blob = new Blob([JSON.stringify(data, null, 2)], {
                type: v.format === 'json' ? 'application/json' : 'text/csv',
              });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `library-export-${new Date().toISOString().split('T')[0]}.${v.format}`;
              a.click();
              URL.revokeObjectURL(url);
              showSuccessToast('Library exported');
            }}
            onClose={actions.closeExportModal}
          />
        );
      })()}

      {state.isImportModalOpen && (() => {
        const importFields = [
          {
            name: 'file',
            type: 'file',
            label: 'File',
            required: true,
            accept: '.json',
            helpText: 'Supports JSON export files.',
          },
          {
            name: '_preview',
            type: 'custom',
            render: () => {
              if (!importPreview) return null;
              if (importPreview.error) {
                return <TypographyMuted>That file isn’t valid JSON.</TypographyMuted>;
              }
              return (
                <TypographyMuted>
                  {importPreview.count} item{importPreview.count !== 1 ? 's' : ''} ready to import.
                </TypographyMuted>
              );
            },
          },
          {
            name: 'mode',
            type: 'select',
            label: 'Import mode',
            options: [
              { value: 'add', label: 'Add new items only (skip duplicates)' },
              { value: 'overwrite', label: 'Overwrite existing items' },
              { value: 'merge', label: 'Merge with existing items' },
            ],
          },
        ];
        const closeImport = () => {
          lastParsedFile.current = null;
          setImportPreview(null);
          actions.closeImportModal();
        };
        return (
          <FormModal
            title="Import Library"
            fields={importFields}
            initialValues={{ mode: 'add' }}
            submitLabel="Start Import"
            submittingLabel="Importing..."
            onValuesChange={(v) => {
              const file = v.file;
              if (!file) {
                lastParsedFile.current = null;
                setImportPreview(null);
                return;
              }
              if (file === lastParsedFile.current) return;
              lastParsedFile.current = file;
              parseImportFile(file)
                .then((data) => setImportPreview({ count: (data.items || []).length }))
                .catch(() => setImportPreview({ error: true }));
            }}
            onSubmit={async (v) => {
              let data;
              try {
                data = await parseImportFile(v.file);
              } catch {
                throw new Error('That file isn’t valid JSON. Please choose a library export file.');
              }
              const result = await libraryService.importLibrary(data.items || [], v.mode);
              await loadItems();
              await loadReferenceData();
              const parts = [`imported ${result.imported_count}`, `skipped ${result.skipped_count}`];
              if (result.failed_count > 0) parts.push(`failed ${result.failed_count}`);
              showToast(result.failed_count > 0 ? 'warning' : 'success', `Import complete — ${parts.join(', ')}.`);
            }}
            onClose={closeImport}
          />
        );
      })()}

      {confirmDialog}
    </main>
  );
}
